import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    BuildOutlined,
    NotificationsOffOutlined,
    WarningAmberOutlined,
} from '@mui/icons-material';
import ActionBubbleTooltip from '../ActionBubbleTooltip';
import api from '../../api';
import { formatDate } from '../../helpers/formatters';
import {
    DUE_SOON_WINDOW_DAYS,
    MAINTENANCE_STATUS_OVERDUE,
    SNOOZE_DAY_OPTIONS,
    countByStatus,
    maintenanceDeadlineLabel,
    maintenanceIntervalLabel,
    maintenanceStatusColor,
    maintenanceStatusLabel,
    printersWithPendingTasks,
    printersWithSnoozedTasks,
} from '../../helpers/maintenanceSchedule';

// Na versão compacta o painel divide espaço com o resto do Dashboard, então
// mostra poucas impressoras e só o que já pede ação.
const COMPACT_PRINTER_LIMIT = 4;
const COMPACT_TASK_LIMIT = 3;

/**
 * Sugestão das próximas manutenções preventivas, cruzando o plano do modelo da
 * impressora com o histórico do usuário e as horas que a máquina rodou.
 *
 * Com `compact`, aparece só quando existe pendência, que é o comportamento que
 * faz sentido no Dashboard.
 */
function UpcomingMaintenancesPanel({ compact = false, reloadKey }) {
    const [printers, setPrinters] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    const [snoozeTarget, setSnoozeTarget] = useState(null);
    const [snoozeAllAnchor, setSnoozeAllAnchor] = useState(null);
    const [showSnoozed, setShowSnoozed] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;
        api.get('printer-maintenances/upcoming')
            .then((response) => {
                if (!active) {
                    return;
                }
                const data = response.data?.data || {};
                setPrinters(data.printers || []);
                setLoaded(true);
            })
            .catch(() => {
                if (active) {
                    setPrinters([]);
                    setLoaded(true);
                }
            });
        return () => {
            active = false;
        };
    }, [reloadKey, refreshTick]);

    const reload = useCallback(() => setRefreshTick((tick) => tick + 1), []);

    const snooze = useCallback((printer, task, days) => {
        setBusy(true);
        api.post('printer-maintenances/snooze', {
            printer_uuid: printer.printer_uuid,
            task_key: task.task_key,
            days,
        })
            .then(reload)
            .catch(() => {})
            .finally(() => {
                setBusy(false);
                setSnoozeTarget(null);
            });
    }, [reload]);

    const snoozeAll = useCallback((days) => {
        setBusy(true);
        api.post('printer-maintenances/snooze-all', { days })
            .then(reload)
            .catch(() => {})
            .finally(() => {
                setBusy(false);
                setSnoozeAllAnchor(null);
            });
    }, [reload]);

    const resume = useCallback((printer, task) => {
        setBusy(true);
        api.post('printer-maintenances/resume', {
            printer_uuid: printer.printer_uuid,
            task_key: task.task_key,
        })
            .then(reload)
            .catch(() => {})
            .finally(() => setBusy(false));
    }, [reload]);

    const pending = useMemo(() => printersWithPendingTasks(printers), [printers]);
    const snoozed = useMemo(() => printersWithSnoozedTasks(printers), [printers]);

    const visible = useMemo(() => {
        if (!compact) {
            return pending;
        }
        return pending
            .slice(0, COMPACT_PRINTER_LIMIT)
            .map((printer) => ({ ...printer, tasks: printer.tasks.slice(0, COMPACT_TASK_LIMIT) }));
    }, [compact, pending]);

    const totals = useMemo(() => countByStatus(visible), [visible]);
    const pendingCount = useMemo(
        () => pending.reduce((total, printer) => total + printer.tasks.length, 0),
        [pending],
    );
    const snoozedCount = useMemo(
        () => snoozed.reduce((total, printer) => total + printer.tasks.length, 0),
        [snoozed],
    );

    // No Dashboard o painel só existe quando há pendência. Na tela de manutenções
    // ele fica de pé também para dar acesso ao que está silenciado.
    const hasContent = visible.length > 0 || (!compact && snoozedCount > 0);
    if (!loaded || !hasContent) {
        return null;
    }

    const hasOverdue = totals.overdue > 0;
    const borderColor = visible.length === 0
        ? 'divider'
        : (hasOverdue ? 'error.main' : 'warning.main');

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                {hasOverdue
                    ? <WarningAmberOutlined color="error" />
                    : <BuildOutlined color={visible.length === 0 ? 'disabled' : 'warning'} />}
                <Typography variant="subtitle1" fontWeight={700}>
                    Próximas manutenções preventivas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {summaryText(totals) || 'nada pendente por agora'}
                </Typography>
                {!compact && pendingCount > 0 && (
                    <Button
                        size="small"
                        startIcon={<NotificationsOffOutlined />}
                        disabled={busy}
                        sx={{ ml: 'auto' }}
                        onClick={(event) => setSnoozeAllAnchor(event.currentTarget)}
                    >
                        Silenciar todas
                    </Button>
                )}
            </Box>

            <Stack divider={<Divider flexItem />} spacing={1.5}>
                {visible.map((printer) => (
                    <Box key={printer.printer_uuid}>
                        <PrinterHeading printer={printer} />

                        <Stack spacing={0.75}>
                            {printer.tasks.map((task) => (
                                <Box
                                    key={task.task_key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        color={maintenanceStatusColor(task.status)}
                                        variant={task.status === MAINTENANCE_STATUS_OVERDUE ? 'filled' : 'outlined'}
                                        label={maintenanceStatusLabel(task.status)}
                                    />
                                    <TaskName task={task} />
                                    <Typography variant="caption" color="text.secondary">
                                        {maintenanceDeadlineLabel(task)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {lastPerformedText(task)}
                                    </Typography>
                                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            size="small"
                                            component={Link}
                                            to={registerLink(printer, task)}
                                        >
                                            Registrar
                                        </Button>
                                        <ActionBubbleTooltip title="Silenciar este aviso por alguns dias">
                                            <IconButton
                                                size="small"
                                                disabled={busy}
                                                aria-label={`Silenciar ${task.label || task.task_key}`}
                                                onClick={(event) => setSnoozeTarget({
                                                    element: event.currentTarget,
                                                    printer,
                                                    task,
                                                })}
                                            >
                                                <NotificationsOffOutlined fontSize="small" />
                                            </IconButton>
                                        </ActionBubbleTooltip>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>

            <Menu
                open={Boolean(snoozeTarget)}
                anchorEl={snoozeTarget?.element}
                onClose={() => setSnoozeTarget(null)}
            >
                <MenuItem disabled sx={{ opacity: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Silenciar por
                    </Typography>
                </MenuItem>
                {SNOOZE_DAY_OPTIONS.map((days) => (
                    <MenuItem
                        key={days}
                        onClick={() => snooze(snoozeTarget.printer, snoozeTarget.task, days)}
                    >
                        {days === 1 ? '1 dia' : `${days} dias`}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                open={Boolean(snoozeAllAnchor)}
                anchorEl={snoozeAllAnchor}
                onClose={() => setSnoozeAllAnchor(null)}
            >
                <MenuItem disabled sx={{ opacity: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Silenciar {pendingCount === 1 ? '1 tarefa' : `${pendingCount} tarefas`} por
                    </Typography>
                </MenuItem>
                {SNOOZE_DAY_OPTIONS.map((days) => (
                    <MenuItem
                        key={`all-${days}`}
                        onClick={() => snoozeAll(days)}
                    >
                        {days === 1 ? '1 dia' : `${days} dias`}
                    </MenuItem>
                ))}
            </Menu>

            {compact && pending.length > COMPACT_PRINTER_LIMIT && (
                <Box sx={{ mt: 1.5 }}>
                    <Button size="small" component={Link} to="/admin/printer-maintenances">
                        Ver todas as impressoras
                    </Button>
                </Box>
            )}

            {!compact && snoozedCount > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Button
                        size="small"
                        startIcon={<NotificationsOffOutlined />}
                        onClick={() => setShowSnoozed((open) => !open)}
                    >
                        {showSnoozed ? 'Ocultar silenciadas' : `Silenciadas (${snoozedCount})`}
                    </Button>
                    <Collapse in={showSnoozed}>
                        <Stack spacing={1.5} sx={{ mt: 1.5 }} divider={<Divider flexItem />}>
                            {snoozed.map((printer) => (
                                <Box key={printer.printer_uuid}>
                                    <PrinterHeading printer={printer} />
                                    <Stack spacing={0.75}>
                                        {printer.tasks.map((task) => (
                                            <Box
                                                key={task.task_key}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={maintenanceStatusLabel(task.status)}
                                                />
                                                <TaskName task={task} />
                                                <Typography variant="caption" color="text.secondary">
                                                    {snoozedUntilText(task)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {maintenanceDeadlineLabel(task)}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    disabled={busy}
                                                    sx={{ ml: 'auto' }}
                                                    onClick={() => resume(printer, task)}
                                                >
                                                    Reativar
                                                </Button>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                            Silenciar apenas esconde o aviso. O prazo e as horas de impressão continuam
                            contando, então o atraso real aparece quando o silêncio terminar.
                        </Typography>
                    </Collapse>
                </Box>
            )}
        </Paper>
    );
}

function PrinterHeading({ printer }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
                {printer.printer_name || 'Impressora'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {[printer.brand, printer.model].filter(Boolean).join(' ')}
            </Typography>
        </Box>
    );
}

/**
 * Nome da tarefa com o intervalo recomendado na tooltip padrão do sistema.
 * Sem intervalo definido não há o que explicar, então nem envolve em tooltip.
 */
function TaskName({ task }) {
    const interval = maintenanceIntervalLabel(task);
    const name = (
        <Typography variant="body2" sx={interval ? { cursor: 'help' } : undefined}>
            {task.label || task.task_key}
        </Typography>
    );
    if (!interval) {
        return name;
    }
    return (
        <ActionBubbleTooltip title={interval} enterTouchDelay={0} leaveTouchDelay={4000}>
            {name}
        </ActionBubbleTooltip>
    );
}

function summaryText({ overdue, dueSoon }) {
    const parts = [];
    if (overdue > 0) {
        parts.push(overdue === 1 ? '1 tarefa atrasada' : `${overdue} tarefas atrasadas`);
    }
    if (dueSoon > 0) {
        parts.push(dueSoon === 1
            ? `1 vence nos próximos ${DUE_SOON_WINDOW_DAYS} dias`
            : `${dueSoon} vencem nos próximos ${DUE_SOON_WINDOW_DAYS} dias`);
    }
    return parts.join(' · ');
}

function snoozedUntilText(task) {
    if (!task.snoozed_until) {
        return '';
    }
    return `silenciada até ${formatDate(task.snoozed_until)}`;
}

function lastPerformedText(task) {
    if (task.never_performed) {
        return 'sem registro anterior';
    }
    if (!task.last_occurred_at) {
        return '';
    }
    return `última em ${formatDate(task.last_occurred_at)}`;
}

function registerLink(printer, task) {
    const params = new URLSearchParams({
        printer_uuid: printer.printer_uuid,
        type: 'manutencao_preventiva',
        problem_description: '.',
        performed_actions: task.label || task.task_key,
    });
    // Só existe sugestão de horímetro quando a manutenção anterior anotou a
    // contagem; sem isso o campo continua em branco para o usuário preencher.
    if (task.suggested_hours_count > 0) {
        params.set('printer_hours_count', String(task.suggested_hours_count));
    }
    return `/admin/register-printer-maintenance?${params.toString()}`;
}

export default UpcomingMaintenancesPanel;
